import React, { useState, useEffect, useCallback } from 'react'
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import {
  getPayments,
  getPaymentById
} from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

function PaymentHistory() {
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Fetch payment data
  const fetchPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPayments({
        page: pagination.currentPage,
        limit: pagination.recordsPerPage,
        search: search,
        status: statusFilter
      });

      if (response.success) {
        setPaymentData(response.data.records);
        setPagination(response.data.pagination);
      } else {
        toast.error('Failed to fetch payment data');
      }
    } catch (error) {
      console.error('Error fetching payment data:', error);
      toast.error('Error fetching payment data');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.recordsPerPage, search, statusFilter]);

  // Load data on component mount and when dependencies change
  useEffect(() => {
    fetchPaymentData();
  }, [fetchPaymentData]);

  const getStatusClass = (status) => {
    const formattedStatus = status?.charAt(0).toUpperCase() + status?.slice(1);
    switch (status?.toLowerCase()) {
      case 'completed':
        return (
          <span className="px-2 py-1 rounded text-sm border border-green-500 bg-green-500/10 text-green-500 inline-flex items-center gap-1">
            <Icon icon="mdi:check-circle-outline" className="text-base" />
            {formattedStatus}
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 rounded text-sm border border-yellow-500 bg-yellow-500/10 text-yellow-500 inline-flex items-center gap-1">
            <Icon icon="mdi:clock-outline" className="text-base" />
            {formattedStatus}
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 rounded text-sm border border-red-500 bg-red-500/10 text-red-500 inline-flex items-center gap-1">
            <Icon icon="mdi:close-circle-outline" className="text-base" />
            {formattedStatus}
          </span>
        );
      case 'refunded':
        return (
          <span className="px-2 py-1 rounded text-sm border border-blue-500 bg-blue-500/10 text-blue-500 inline-flex items-center gap-1">
            <Icon icon="mdi:refresh-circle-outline" className="text-base" />
            {formattedStatus}
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded text-sm border border-gray-500 bg-gray-500/10 text-gray-500 inline-flex items-center gap-1">
            <Icon icon="mdi:help-circle-outline" className="text-base" />
            Unknown
          </span>
        );
    }
  };

  const handleViewDetails = async (paymentId) => {
    if (!permissions.settings_payments_view_analytics) {
      toast.warning("You don't have permission to view payment details");
      return;
    }

    try {
      setLoading(true);
      const response = await getPaymentById(paymentId);
      if (response.success) {
        if (response.data.receipt_url) {
          window.open(response.data.receipt_url, '_blank');
        } else if (response.data.receipt_pdf_path) {
          const pdfUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${response.data.receipt_pdf_path}`;
          window.open(pdfUrl, '_blank');
        } else {
          toast.warning('Invoice not available for this payment');
        }
      } else {
        toast.error('Failed to fetch payment details');
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Error fetching payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (paymentId) => {
    if (!permissions.settings_payments_view_analytics) {
      toast.warning("You don't have permission to view payment details");
      return;
    }

    try {
      setLoading(true);
      const response = await getPaymentById(paymentId);
      if (response.success) {
        setSelectedPayment(response.data);
        setShowInvoiceModal(true);
      } else {
        toast.error('Failed to fetch payment details');
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Error fetching payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = (payment) => {
    if (payment.receipt_url) {
      window.open(payment.receipt_url, '_blank');
    } else if (payment.receipt_pdf_path) {
      // Construct the full URL for the PDF path
      const pdfUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${payment.receipt_pdf_path}`;
      window.open(pdfUrl, '_blank');
    } else {
      toast.warning('Invoice not available for this payment');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({
      ...prev,
      recordsPerPage: newLimit,
      currentPage: 1
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <>
      {/* search and filter section */}
      <div className="grid grid-cols-12 gap-4 mb-3">
        <div className="col-span-12 md:col-span-6 mt-2 flex justify-start items-center gap-2">
          <input
            type="search"
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 max-w-[350px]"
            placeholder="Search by course name, payment method, or payment ID..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="col-span-12 md:col-span-6 text-right mt-2 flex justify-end items-center gap-2">
          <select
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 w-[180px]"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>
      </div>

      {/* table */}
      <div className="grid grid-cols-12 gap-4 mb-3">
        <div className="col-span-12">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="overflow-x-auto">
              <table className="w-full border-0 mb-0" style={{ minWidth: '1200px' }}>
                <thead>
                  <tr>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '80px' }}>SL No</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '150px' }}>Transaction ID</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '120px' }}>Amount</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '180px' }}>Payment Date</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '120px' }}>Status</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '150px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paymentData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        No payment records found
                      </td>
                    </tr>
                  ) : (
                    paymentData.map((payment, index) => (
                      <tr key={payment.id} className="border-b">
                        <td className="py-3 align-middle">
                          {(pagination.currentPage - 1) * pagination.recordsPerPage + index + 1}
                        </td>
                        <td className="py-3 align-middle">
                          {payment.payment_uid ? payment.payment_uid.substring(0, 20) + '...' : 'N/A'}
                        </td>
                        <td className="py-3 align-middle">
                          {formatAmount(payment.amount, payment.currency)}
                        </td>
                        <td className="py-3 align-middle">{formatDate(payment.created_at)}</td>
                        <td className="py-3 align-middle">
                          {getStatusClass(payment.status)}
                        </td>
                        <td className="py-3 align-middle">
                          <div className="flex gap-2">
                            <button
                              className="border border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white text-sm py-1 px-2 rounded w-9 h-9 flex items-center justify-center"
                              title={!permissions.settings_payments_view_analytics ? "You don't have permission to view invoice" : "Open Invoice"}
                              onClick={() => handleViewDetails(payment.id)}
                              disabled={loading || !permissions.settings_payments_view_analytics}
                            >
                              <Icon icon="fluent:document-pdf-24-regular" width="16" height="16" />
                            </button>
                            <button
                              className="border border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white text-sm py-1 px-2 rounded w-9 h-9 flex items-center justify-center"
                              title={!permissions.settings_payments_view_analytics ? "You don't have permission to view details" : "View Details"}
                              onClick={() => handleViewInvoice(payment.id)}
                              disabled={loading || !permissions.settings_payments_view_analytics}
                            >
                              <Icon icon="fluent:eye-24-regular" width="16" height="16" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* record per page and pagination */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          <select
            className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 w-auto"
            value={pagination.recordsPerPage}
            onChange={handleLimitChange}
          >
            <option value={10}>10 records per page</option>
            <option value={25}>25 records per page</option>
            <option value={50}>50 records per page</option>
          </select>
        </div>
        <div className="col-span-12 md:col-span-6 flex justify-end items-center gap-3">
          <button
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-[150px]"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
          >
            Prev
          </button>
          <span>Page {pagination.currentPage} of {pagination.totalPages}</span>
          <button
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-[150px]"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>

      {/* Payment Details Modal */}
      {showDetailsModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" tabIndex="-1">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4">
            <div className="p-4 border-b border-0 pb-0">
              <div className="flex justify-between items-center">
                <h5 className="text-lg font-semibold">Payment Details</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedPayment(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-4 pt-2">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Transaction ID:</label>
                    <p className="mb-0">{selectedPayment.payment_uid ? selectedPayment.payment_uid.substring(0, 20) + '...' : 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">User Name:</label>
                    <p className="mb-0">{selectedPayment.user_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Phone Number:</label>
                    <p className="mb-0">{selectedPayment.user_phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Email:</label>
                    <p className="mb-0">{selectedPayment.user_email || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Payment Method:</label>
                    <p className="mb-0">{selectedPayment.payment_method}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Course Name:</label>
                    <p className="mb-0">{selectedPayment.course_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Status:</label>
                    <div>
                      {getStatusClass(selectedPayment.status)}
                    </div>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Amount:</label>
                    <p className="mb-0">{formatAmount(selectedPayment.amount, selectedPayment.currency)}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Payment Date:</label>
                    <p className="mb-0">{formatDate(selectedPayment.created_at)}</p>
                  </div>
                </div>
                {(selectedPayment.receipt_url || selectedPayment.receipt_pdf_path) && (
                  <div className="col-span-12">
                    <div className="flex justify-center mt-3">
                      <button
                        className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                        onClick={() => handleDownloadInvoice(selectedPayment)}
                      >
                        <Icon icon="fluent:document-pdf-24-regular" width="16" height="16" />
                        Open Invoice
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedPayment(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && selectedPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" tabIndex="-1">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4">
            <div className="p-4 border-b border-0 pb-0">
              <div className="flex justify-between items-center">
                <h5 className="text-lg font-semibold">Invoice Details</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedPayment(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-4 pt-2">
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 mt-2 mb-2">
                  {(selectedPayment.receipt_url || selectedPayment.receipt_pdf_path) && (
                    <button
                      className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg w-full flex items-center justify-center gap-2"
                      onClick={() => handleDownloadInvoice(selectedPayment)}
                    >
                      <Icon icon="fluent:document-pdf-24-regular" width="16" height="16" />
                      Open Invoice in New Tab
                    </button>
                  )}
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Transaction ID:</label>
                    <p className="mb-0 break-all">{selectedPayment.payment_uid || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">User Name:</label>
                    <p className="mb-0">{selectedPayment.user_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Phone Number:</label>
                    <p className="mb-0">{selectedPayment.user_phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Email:</label>
                    <p className="mb-0">{selectedPayment.user_email || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Payment Method:</label>
                    <p className="mb-0">{selectedPayment.payment_method}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Course Name:</label>
                    <p className="mb-0">{selectedPayment.course_name || 'N/A'}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Status:</label>
                    <div>
                      {getStatusClass(selectedPayment.status)}
                    </div>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Amount:</label>
                    <p className="mb-0">{formatAmount(selectedPayment.amount, selectedPayment.currency)}</p>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="mb-3">
                    <label className="block mb-1 font-bold">Payment Date:</label>
                    <p className="mb-0">{formatDate(selectedPayment.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                type="button"
                className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setSelectedPayment(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PaymentHistory
